import { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import differenceInDays from "date-fns/differenceInDays";
import { useFormContext } from "react-hook-form";
import { useDebounced } from "../../../../hooks/useDebounce";
import {
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  LINKEDIN,
} from "../../../../utils/constants";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetEstimationAudienceAdsCampaign } from "../../../../actions/adsCampaigns";
import { numberCompactFormat } from "../../../../utils/numbers";
import {
  AUDIENCE_SIZE,
  AUDIENCE_ESTIMATES,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import CardContainer from "../../../Card/CardContainer";

const AudienceEstimatesPreviewLinkedin = ({ BASIC_PATH }) => {
  const {
    watch,
    getValues,
    audiencePreviewsCache,
    onChangeAudiencePreviewsCache,
  } = useFormContext();
  const abortControllerRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [estimationData, setEstimationData] = useState({});
  const [errorGenerateEstimation, setErrorGenerateEstimation] = useState(null);

  // Watch form data
  const typeBudget = watch(`${BASIC_PATH}.typeBudget`);
  const budget = watch(`${BASIC_PATH}.budget`);
  const startDate = watch(`${BASIC_PATH}.startDate`);
  const endDate = watch(`${BASIC_PATH}.endDate`);

  const locations = watch(`${BASIC_PATH}.locations`);
  const genders = watch(`${BASIC_PATH}.genders`);
  const ageRange = watch(`${BASIC_PATH}.ageRange`);
  const interests = watch(`${BASIC_PATH}.interests`);
  const behaviors = watch(`${BASIC_PATH}.behaviors`);
  const customAudiences = watch(`${BASIC_PATH}.customAudiences`);
  const excludedCustomAudiences = watch(
    `${BASIC_PATH}.excludedCustomAudiences`
  );
  const languages = watch(`${BASIC_PATH}.languages`);

  const seniorities = watch(`${BASIC_PATH}.seniorities`);
  const industries = watch(`${BASIC_PATH}.industries`);
  const jobFunctions = watch(`${BASIC_PATH}.jobFunctions`);
  const jobTitles = watch(`${BASIC_PATH}.jobTitles`);
  const companySizes = watch(`${BASIC_PATH}.companySizes`);
  const companies = watch(`${BASIC_PATH}.companies`);
  const degrees = watch(`${BASIC_PATH}.degrees`);
  const fieldsOfStudy = watch(`${BASIC_PATH}.fieldsOfStudy`);
  const schools = watch(`${BASIC_PATH}.schools`);
  const skills = watch(`${BASIC_PATH}.skills`);
  const memberBehaviors = watch(`${BASIC_PATH}.memberBehaviors`);

  const platformData = getValues(`platformsData.${LINKEDIN}`);
  const integrationID = platformData?.integrationID;
  const adAccountID = platformData?.adAccountID;
  const objective = platformData?.objective;

  const cachedData = audiencePreviewsCache?.[BASIC_PATH] || null;

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const onGetAudienceEstimation = async (type) => {
    onChangeAudiencePreviewsCache({
      ...audiencePreviewsCache,
      [BASIC_PATH]: null,
    });

    if (type === "first") {
      if (cachedData) {
        setEstimationData(cachedData);
        setIsLoading(false);
        setIsFirstLoading(false);
        return;
      }
      setIsFirstLoading(false);
    }

    // Guard: without account/integration we can't estimate
    if (!integrationID || !adAccountID) {
      setErrorGenerateEstimation(null);
      setEstimationData({});
      setIsLoading(false);
      return;
    }

    // Abort previous request if exists
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);

    let finalBudget;
    if (typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION) {
      finalBudget = budget;
    } else {
      if (startDate && endDate) {
        const days = differenceInDays(endDate, startDate);
        finalBudget = days > 0 ? Math.round(budget / days) : 0;
      } else {
        finalBudget = 0;
      }
    }

    const response = await dispatch(
      startGetEstimationAudienceAdsCampaign(
        {
          platform: LINKEDIN,
          integrationID,
          adAccountID,
          objective,
          budget: finalBudget,
          locations,
          genders,
          ageRange,
          interests,
          behaviors,
          customAudiences,
          excludedCustomAudiences,
          languages,
          seniorities,
          industries,
          jobFunctions,
          jobTitles,
          companySizes,
          companies,
          degrees,
          fieldsOfStudy,
          schools,
          skills,
          memberBehaviors,
        },
        abortControllerRef.current.signal
      )
    );

    if (!response.ok) {
      if (response.aborted) {
        setIsLoading(false);
        return;
      }
      setErrorGenerateEstimation(response.message);
    }

    if (response.ok) {
      const data = response.data;
      const newEstimationData = data;

      setEstimationData(newEstimationData);
      setErrorGenerateEstimation(null);

      onChangeAudiencePreviewsCache({
        ...audiencePreviewsCache,
        [BASIC_PATH]: newEstimationData,
      });
    }

    setIsLoading(false);
  };

  const debouncedEstimationRequest = useDebounced(
    onGetAudienceEstimation,
    3000
  );

  useEffect(() => {
    setErrorGenerateEstimation(null);

    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }

    debouncedEstimationRequest(isFirstLoading ? "first" : "change");

    // eslint-disable-next-line
  }, [
    typeBudget,
    budget,
    startDate,
    endDate,
    locations,
    genders,
    ageRange,
    interests,
    behaviors,
    customAudiences,
    excludedCustomAudiences,
    languages,
    seniorities,
    industries,
    jobFunctions,
    jobTitles,
    companySizes,
    companies,
    degrees,
    fieldsOfStudy,
    schools,
    skills,
    memberBehaviors,
    integrationID,
    adAccountID,
    objective,
  ]);

  useEffect(() => {
    return () => {
      if (abortControllerRef?.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <CardContainer
      sxContainer={{
        boxShadow: "none !important",
        mt: 0.5,
        mb: 0.5,
      }}
      title={
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t(AUDIENCE_ESTIMATES)}
        </Typography>
      }
    >
      {errorGenerateEstimation && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {errorGenerateEstimation}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2">{t(AUDIENCE_SIZE)}</Typography>
          {isLoading ? (
            <Skeleton variant="text" width="60%" height={24} />
          ) : (
            <Typography variant="body1">
              {numberCompactFormat(estimationData.maxSize || 0)}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContainer>
  );
};

export default AudienceEstimatesPreviewLinkedin;
